"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Brain,
  CheckCircle2,
  Clock,
  Database,
  ExternalLink,
  Eye,
  FileText,
  GraduationCap,
  Layers3,
  Lock,
  Moon,
  MousePointerClick,
  PlayCircle,
  Plus,
  RefreshCw,
  ShieldCheck,
  Video,
  Waypoints,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminToolLearningArtifactUrl } from "@/src/data/admin-console-api";
import type { AgentProfile } from "@/lib/aura/types";
import type {
  AdminAgentDaemonContract,
  AdminToolAgentAssignment,
  AdminToolCatalogRecord,
  AdminToolConsolidationRun,
  AdminToolCreateInput,
  AdminToolLearningArtifactStoreInput,
  AdminToolLearningEvidenceAppendInput,
  AdminToolLearningMemoryPromotionInput,
  AdminToolLearningSession,
  AdminToolLearningSessionCreateInput,
  AdminToolModuleContract,
  AdminToolsState,
  AdminToolTeachingMode,
} from "@/src/data/admin-console-api";

interface ToolsStudioProps {
  toolsState: AdminToolsState | null;
  agents: AgentProfile[];
  selectedToolId: string | null;
  loading?: boolean;
  onRefresh: () => void;
  onSelectTool: (toolId: string) => void;
  onCreateTool: (request: AdminToolCreateInput) => Promise<void> | void;
  onAssignAgent: (request: {
    toolId: string;
    moduleIds: string[];
    agentId: string;
    auditorAgentId?: string;
    visibility?: "private" | "workspace" | "tenant";
  }) => Promise<void> | void;
  onCreateLearningSession: (
    request: AdminToolLearningSessionCreateInput
  ) => Promise<AdminToolLearningSession | void> | AdminToolLearningSession | void;
  onAppendLearningEvidence: (
    request: AdminToolLearningEvidenceAppendInput
  ) => Promise<AdminToolLearningSession | void> | AdminToolLearningSession | void;
  onUploadLearningArtifact: (
    request: AdminToolLearningArtifactStoreInput
  ) => Promise<AdminToolLearningSession | void> | AdminToolLearningSession | void;
  onPromoteLearningMemory: (
    request: AdminToolLearningMemoryPromotionInput
  ) => Promise<AdminToolLearningSession | void> | AdminToolLearningSession | void;
  onQueueOperatorCommand: (
    kind:
      | "browser_dry_run"
      | "api_dry_run"
      | "desktop_dry_run"
      | "sap_browser_readiness_dry_run"
      | "sap_api_readiness_dry_run"
      | "sap_desktop_readiness_dry_run",
    metadata?: {
      learningSessionId?: string;
      requestedScopes?: string[];
    }
  ) => Promise<string>;
  onCreateConsolidationRun: (request: {
    assignmentId: string;
    agentId: string;
    toolId: string;
    scheduledFor?: string;
  }) => Promise<void> | void;
  onOpenCommandAura: (agentId?: string) => void;
  onOpenDecisionRoom: (toolId?: string) => void;
}

type WebActionTraceEvent = {
  id: string;
  kind: "navigate" | "click" | "input" | "submit";
  capturedAt: string;
  url: string;
  title: string;
  selector?: string;
  text?: string;
  role?: string;
  tagName?: string;
  href?: string;
  pointer?: {
    x: number;
    y: number;
  };
  valueLength?: number;
  valueRedacted?: boolean;
};

type WebActionTraceArtifact = {
  schemaVersion: 1;
  kind: "climb-one.web-action-trace";
  sessionId: string;
  toolId: string;
  moduleId: string;
  teachingMode: AdminToolTeachingMode;
  startedAt: string;
  stoppedAt: string;
  source: "admin_console_browser_surface";
  secretPolicy: {
    rawInputValuesStored: false;
    credentialsAllowed: false;
    providerNativeMemoryAllowed: false;
  };
  events: WebActionTraceEvent[];
};

export function ToolsStudio({
  toolsState,
  agents,
  selectedToolId,
  loading,
  onRefresh,
  onSelectTool,
  onCreateTool,
  onAssignAgent,
  onCreateLearningSession,
  onAppendLearningEvidence,
  onUploadLearningArtifact,
  onPromoteLearningMemory,
  onQueueOperatorCommand,
  onCreateConsolidationRun,
  onOpenCommandAura,
  onOpenDecisionRoom,
}: ToolsStudioProps) {
  const tools = toolsState?.tools ?? [];
  const assignments = toolsState?.assignments ?? [];
  const learningSessions = toolsState?.learningSessions ?? [];
  const consolidationRuns = toolsState?.consolidationRuns ?? [];
  const selectedTool = tools.find((tool) => tool.id === selectedToolId) ?? tools[0] ?? null;
  const selectedAssignments = selectedTool
    ? assignments.filter((assignment) => assignment.toolId === selectedTool.id)
    : [];
  const selectedLearningSessions = selectedTool
    ? learningSessions.filter((session) => session.toolId === selectedTool.id)
    : [];
  const selectedConsolidationRuns = selectedTool
    ? consolidationRuns.filter((run) => run.toolId === selectedTool.id)
    : [];

  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <header className="shrink-0 border-b border-border/30 px-4 py-4 lg:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Tools / Expert Tools
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
              Governed tool learning and agent daemons
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
              Tenant tools, modules, agent assignments, learning evidence, and memory consolidation come from CLIMB ONE Core.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-border/50 bg-card/60 px-3 py-1 text-xs text-muted-foreground">
              {toolsState?.source?.tenantScoped ? "tenant scoped" : "source unavailable"}
            </span>
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
              <RefreshCw className={cn("mr-2 h-4 w-4", loading && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <section className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[320px_minmax(0,1fr)]">
        <ToolsCatalog
          tools={tools}
          assignments={assignments}
          selectedToolId={selectedTool?.id ?? null}
          onSelectTool={onSelectTool}
          onCreateTool={onCreateTool}
        />
        <div className="min-h-0 overflow-y-auto px-4 py-4 pb-24 lg:px-6 lg:pb-8">
          {!selectedTool ? (
            <EmptyTools loading={loading} />
          ) : (
            <div className="space-y-4">
              <ToolHeader
                tool={selectedTool}
                assignmentCount={selectedAssignments.length}
                learningCount={selectedLearningSessions.length}
                consolidationCount={selectedConsolidationRuns.length}
                onOpenCommandAura={() => onOpenCommandAura()}
                onOpenDecisionRoom={() => onOpenDecisionRoom(selectedTool.id)}
              />
              <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
                <div className="space-y-4">
                  <ToolModules modules={selectedTool.modules} />
                  <TeachingLearningStudio
                    tool={selectedTool}
                    assignments={selectedAssignments}
                    sessions={selectedLearningSessions}
                    onCreateLearningSession={onCreateLearningSession}
                    onAppendLearningEvidence={onAppendLearningEvidence}
                    onUploadLearningArtifact={onUploadLearningArtifact}
                    onPromoteLearningMemory={onPromoteLearningMemory}
                    onQueueOperatorCommand={onQueueOperatorCommand}
                  />
                  <RoleVisibilityControls tool={selectedTool} />
                </div>
                <div className="space-y-4">
                  <AssignAgentPanel
                    tool={selectedTool}
                    agents={agents}
                    assignments={selectedAssignments}
                    onAssignAgent={onAssignAgent}
                  />
                  <AgentDaemonPanel
                    assignments={selectedAssignments}
                    agents={agents}
                    consolidationRuns={selectedConsolidationRuns}
                    onCreateConsolidationRun={onCreateConsolidationRun}
                    onOpenCommandAura={onOpenCommandAura}
                  />
                  <MemoryConsolidationPanel
                    assignments={selectedAssignments}
                    consolidationRuns={selectedConsolidationRuns}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function ToolsCatalog({
  tools,
  assignments,
  selectedToolId,
  onSelectTool,
  onCreateTool,
}: {
  tools: AdminToolCatalogRecord[];
  assignments: AdminToolAgentAssignment[];
  selectedToolId: string | null;
  onSelectTool: (toolId: string) => void;
  onCreateTool: ToolsStudioProps["onCreateTool"];
}) {
  const [creating, setCreating] = useState(false);

  return (
    <aside className="min-h-0 border-b border-border/30 bg-card/20 lg:border-b-0 lg:border-r">
      <div className="sticky top-0 z-10 border-b border-border/30 bg-card/80 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Catalog
          </p>
          <span className="rounded-full bg-muted/60 px-2 py-0.5 text-xs text-muted-foreground">
            {tools.length}
          </span>
        </div>
      </div>
      <div className="max-h-[260px] space-y-2 overflow-y-auto p-3 lg:max-h-none lg:h-[calc(100dvh-9.5rem)]">
        <Button
          variant="outline"
          size="sm"
          className="mb-2 w-full justify-center"
          onClick={() => setCreating((current) => !current)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Tool
        </Button>
        {creating && (
          <CreateToolPanel
            onCreateTool={async (request) => {
              await onCreateTool(request);
              setCreating(false);
            }}
          />
        )}
        {tools.map((tool) => {
          const isActive = tool.id === selectedToolId;
          const assignmentCount = assignments.filter((assignment) => assignment.toolId === tool.id).length;

          return (
            <button
              key={tool.id}
              onClick={() => onSelectTool(tool.id)}
              className={cn(
                "w-full rounded-xl border px-3 py-3 text-left transition-all",
                isActive
                  ? "border-cyan-300/35 bg-cyan-300/10 shadow-[0_0_32px_rgba(103,232,249,0.08)]"
                  : "border-border/35 bg-card/35 hover:border-border/70 hover:bg-card/60"
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{tool.displayName}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{tool.domainId}</p>
                </div>
                <StatusPill value={tool.status} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                <Metric label="modules" value={tool.modules.length} />
                <Metric label="daemons" value={assignmentCount} />
              </div>
            </button>
          );
        })}
      </div>
    </aside>
  );
}

function ToolHeader({
  tool,
  assignmentCount,
  learningCount,
  consolidationCount,
  onOpenCommandAura,
  onOpenDecisionRoom,
}: {
  tool: AdminToolCatalogRecord;
  assignmentCount: number;
  learningCount: number;
  consolidationCount: number;
  onOpenCommandAura: () => void;
  onOpenDecisionRoom: () => void;
}) {
  return (
    <section className="rounded-2xl border border-border/35 bg-card/45 p-5 shadow-2xl shadow-black/10">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill value={tool.status} />
            <StatusPill value={tool.visibility} tone="muted" />
            <StatusPill value={tool.decisionRoomToolDomain} tone="cyan" />
          </div>
          <h2 className="mt-4 text-2xl font-semibold tracking-tight text-foreground">{tool.displayName}</h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">{tool.description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onOpenCommandAura}>
            Open Command AURA
          </Button>
          <Button size="sm" onClick={onOpenDecisionRoom}>
            Open Decision Room
          </Button>
        </div>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <MetricCard icon={Layers3} label="Modules" value={tool.modules.length} />
        <MetricCard icon={Waypoints} label="Agent daemons" value={assignmentCount} />
        <MetricCard icon={Brain} label="Learning records" value={learningCount + consolidationCount} />
      </div>
    </section>
  );
}

function CreateToolPanel({
  onCreateTool,
}: {
  onCreateTool: ToolsStudioProps["onCreateTool"];
}) {
  const [displayName, setDisplayName] = useState("");
  const [domainId, setDomainId] = useState("");
  const [description, setDescription] = useState("");
  const [moduleName, setModuleName] = useState("");
  const [moduleCategory, setModuleCategory] = useState<AdminToolModuleContract["category"]>("technical");
  const [connectorTypes, setConnectorTypes] = useState("browser,api,custom");
  const [teachingModes, setTeachingModes] = useState("conversation,document_upload,human_demonstration,screen_recording,api_trace,routine_replay");
  const [allowedRoleIds, setAllowedRoleIds] = useState("tenant_owner,admin_console_admin,workspace_admin");

  return (
    <section className="mb-3 rounded-xl border border-cyan-300/25 bg-cyan-300/10 p-3">
      <p className="text-xs font-medium uppercase tracking-[0.14em] text-cyan-100">
        New tenant tool
      </p>
      <div className="mt-3 space-y-2">
        <TextField label="Name" value={displayName} onChange={setDisplayName} placeholder="AWS, SAP, GitHub, Customer Tool" />
        <TextField label="Domain id" value={domainId} onChange={setDomainId} placeholder="aws, sap, github, customer-domain" />
        <TextField label="Description" value={description} onChange={setDescription} placeholder="What this tool teaches and governs" />
        <TextField label="First module" value={moduleName} onChange={setModuleName} placeholder="Administration, Integration, Support Routine" />
        <SelectField label="Module category" value={moduleCategory} onChange={(value) => setModuleCategory(value as typeof moduleCategory)}>
          <option value="technical">Technical</option>
          <option value="functional">Functional</option>
          <option value="hybrid">Hybrid</option>
        </SelectField>
        <TextField label="Connector types" value={connectorTypes} onChange={setConnectorTypes} placeholder="browser,api,custom" />
        <TextField label="Teaching modes" value={teachingModes} onChange={setTeachingModes} placeholder="conversation,document_upload" />
        <TextField label="Allowed roles" value={allowedRoleIds} onChange={setAllowedRoleIds} placeholder="tenant_owner,workspace_admin" />
        <Button
          className="w-full"
          disabled={!displayName.trim() || !domainId.trim() || !description.trim() || !moduleName.trim()}
          onClick={() =>
            onCreateTool({
              displayName,
              domainId: slugInput(domainId),
              description,
              decisionRoomToolDomain: slugInput(domainId) === "sap" ? "sap" : "other",
              visibility: "workspace",
              status: "draft",
              allowedRoleIds: splitCsv(allowedRoleIds),
              modules: [
                {
                  id: slugInput(moduleName),
                  displayName: moduleName,
                  category: moduleCategory,
                  description: `${moduleName} operating module for ${displayName}.`,
                  surfaces: ["browser"],
                  connectorTypes: splitCsv(connectorTypes) as AdminToolModuleContract["connectorTypes"],
                  teachingModes: splitCsv(teachingModes) as AdminToolModuleContract["teachingModes"],
                  approvalMode: "required_before_external_action",
                  evidenceRequired: ["objective", "sanitized_evidence", "approval_boundary"],
                  memoryWritePolicy: "manual_approval",
                  rawSecretsVisibleByDefault: false,
                },
              ],
            })
          }
        >
          Create through Core
        </Button>
      </div>
    </section>
  );
}

function ToolModules({ modules }: { modules: AdminToolModuleContract[] }) {
  const groups = groupModules(modules);

  return (
    <section className="rounded-2xl border border-border/35 bg-card/35 p-4">
      <PanelTitle icon={Layers3} title="Tool modules" detail="Technical, functional, and hybrid operating contracts." />
      <div className="mt-4 grid gap-3 xl:grid-cols-3">
        {groups.map((group) => (
          <div key={group.category} className="rounded-xl border border-border/25 bg-background/25 p-3">
            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
              {formatLabel(group.category)}
            </p>
            <div className="mt-3 space-y-3">
              {group.modules.map((module) => (
                <article key={module.id} className="rounded-lg bg-card/45 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-medium text-foreground">{module.displayName}</h3>
                    <StatusPill value={module.approvalMode} tone="amber" />
                  </div>
                  <p className="mt-2 text-xs leading-5 text-muted-foreground">{module.description}</p>
                  <ChipRow values={module.connectorTypes} />
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Memory write: <span className="text-foreground">{formatLabel(module.memoryWritePolicy)}</span>
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Evidence: {module.evidenceRequired.join(", ")}
                  </p>
                </article>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AssignAgentPanel({
  tool,
  agents,
  assignments,
  onAssignAgent,
}: {
  tool: AdminToolCatalogRecord;
  agents: AgentProfile[];
  assignments: AdminToolAgentAssignment[];
  onAssignAgent: ToolsStudioProps["onAssignAgent"];
}) {
  const [agentId, setAgentId] = useState(agents[0]?.identity.id ?? "");
  const [auditorAgentId, setAuditorAgentId] = useState("");
  const [moduleIds, setModuleIds] = useState<string[]>(tool.modules.slice(0, 1).map((module) => module.id));
  const [visibility, setVisibility] = useState<"private" | "workspace" | "tenant">(tool.visibility);

  return (
    <section className="rounded-2xl border border-border/35 bg-card/35 p-4">
      <PanelTitle icon={Plus} title="Assign agent" detail="Creates the governed daemon contract through Core." />
      <div className="mt-4 space-y-3">
        <SelectField label="Executor agent" value={agentId} onChange={setAgentId}>
          {agents.map((agent) => (
            <option key={agent.identity.id} value={agent.identity.id}>
              {agent.identity.displayName}
            </option>
          ))}
        </SelectField>
        <SelectField label="Auditor" value={auditorAgentId} onChange={setAuditorAgentId}>
          <option value="">Core selected / none</option>
          {agents.map((agent) => (
            <option key={agent.identity.id} value={agent.identity.id}>
              {agent.identity.displayName}
            </option>
          ))}
        </SelectField>
        <SelectField label="Visibility" value={visibility} onChange={(value) => setVisibility(value as typeof visibility)}>
          <option value="private">Private</option>
          <option value="workspace">Workspace</option>
          <option value="tenant">Tenant</option>
        </SelectField>
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">Modules</p>
          <div className="grid gap-2">
            {tool.modules.map((module) => {
              const checked = moduleIds.includes(module.id);
              return (
                <label key={module.id} className="flex items-center gap-2 rounded-lg border border-border/25 bg-background/25 px-3 py-2 text-xs text-foreground">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      setModuleIds((current) =>
                        event.target.checked
                          ? [...current, module.id]
                          : current.filter((id) => id !== module.id)
                      );
                    }}
                  />
                  {module.displayName}
                </label>
              );
            })}
          </div>
        </div>
        <Button
          className="w-full"
          disabled={!agentId || moduleIds.length === 0}
          onClick={() =>
            onAssignAgent({
              toolId: tool.id,
              moduleIds,
              agentId,
              auditorAgentId: auditorAgentId || undefined,
              visibility,
            })
          }
        >
          Register through Core
        </Button>
        <div className="rounded-lg border border-border/25 bg-background/25 p-3 text-xs text-muted-foreground">
          Existing assignments: <span className="text-foreground">{assignments.length}</span>
        </div>
      </div>
    </section>
  );
}

function AgentDaemonPanel({
  assignments,
  agents,
  consolidationRuns,
  onCreateConsolidationRun,
  onOpenCommandAura,
}: {
  assignments: AdminToolAgentAssignment[];
  agents: AgentProfile[];
  consolidationRuns: AdminToolConsolidationRun[];
  onCreateConsolidationRun: ToolsStudioProps["onCreateConsolidationRun"];
  onOpenCommandAura: (agentId?: string) => void;
}) {
  return (
    <section className="rounded-2xl border border-border/35 bg-card/35 p-4">
      <PanelTitle icon={Waypoints} title="Agent daemon" detail="Founder supervises. AURA governs visibly. Agent identity remains separate." />
      <div className="mt-4 space-y-3">
        {assignments.length === 0 ? (
          <p className="rounded-lg border border-border/25 bg-background/25 p-3 text-sm text-muted-foreground">
            No daemon assignment exists yet for this tool.
          </p>
        ) : (
          assignments.map((assignment) => {
            const agent = agents.find((item) => item.identity.id === assignment.agentId);
            const latestRun = consolidationRuns.find((run) => run.assignmentId === assignment.id);

            return (
              <article key={assignment.id} className="rounded-xl border border-border/25 bg-background/25 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{agent?.identity.displayName ?? assignment.agentId}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{assignment.daemon.runtimeBase}</p>
                  </div>
                  <StatusPill value={assignment.daemon.loopStatus} tone="cyan" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <Metric label="supervisor" value="Founder Agent" />
                  <Metric label="governor" value="AURA" />
                  <Metric label="heartbeat" value={formatDate(assignment.daemon.heartbeatAt)} />
                  <Metric label="next wake" value={formatDate(assignment.daemon.nextWakeAt)} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => onOpenCommandAura(assignment.agentId)}>
                    Command agent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onCreateConsolidationRun({
                        assignmentId: assignment.id,
                        agentId: assignment.agentId,
                        toolId: assignment.toolId,
                      })
                    }
                  >
                    Start consolidation
                  </Button>
                </div>
                {latestRun && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Latest consolidation: <span className="text-foreground">{formatLabel(latestRun.status)}</span>
                  </p>
                )}
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}

function TeachingLearningStudio({
  tool,
  assignments,
  sessions,
  onCreateLearningSession,
  onAppendLearningEvidence,
  onUploadLearningArtifact,
  onPromoteLearningMemory,
  onQueueOperatorCommand,
}: {
  tool: AdminToolCatalogRecord;
  assignments: AdminToolAgentAssignment[];
  sessions: AdminToolLearningSession[];
  onCreateLearningSession: ToolsStudioProps["onCreateLearningSession"];
  onAppendLearningEvidence: ToolsStudioProps["onAppendLearningEvidence"];
  onUploadLearningArtifact: ToolsStudioProps["onUploadLearningArtifact"];
  onPromoteLearningMemory: ToolsStudioProps["onPromoteLearningMemory"];
  onQueueOperatorCommand: ToolsStudioProps["onQueueOperatorCommand"];
}) {
  const firstModule = tool.modules[0];
  const teachingModes = unique(tool.modules.flatMap((module) => module.teachingModes));
  const [moduleId, setModuleId] = useState(firstModule?.id ?? "");
  const [teachingMode, setTeachingMode] = useState<AdminToolTeachingMode>(teachingModes[0] ?? "conversation");
  const [title, setTitle] = useState("");
  const [objective, setObjective] = useState("");
  const [apiTrace, setApiTrace] = useState("");
  const [humanCaptureMode, setHumanCaptureMode] = useState<"external_screen" | "controlled_browser" | "portal_trace">("external_screen");
  const [captureStatus, setCaptureStatus] = useState<string | null>(null);
  const [recording, setRecording] = useState<MediaRecorder | null>(null);
  const [actionTraceRecording, setActionTraceRecording] = useState<{
    sessionId: string;
    startedAt: string;
  } | null>(null);
  const actionTraceEventsRef = useRef<WebActionTraceEvent[]>([]);
  const stopActionTraceRef = useRef<(() => void) | null>(null);
  const documentInputRef = useRef<HTMLInputElement | null>(null);
  const assignment = assignments[0];
  const activeSession = sessions[0];

  const ensureSession = async () => {
    const requestedTitle = title.trim();
    const requestedObjective = objective.trim();
    const canReuseActiveSession =
      activeSession &&
      activeSession.moduleId === moduleId &&
      activeSession.teachingMode === teachingMode &&
      !requestedTitle &&
      !requestedObjective;

    if (canReuseActiveSession) {
      return activeSession;
    }

    const session = await onCreateLearningSession({
      toolId: tool.id,
      moduleId,
      assignmentId: assignment?.id,
      agentId: assignment?.agentId,
      title: requestedTitle || `${formatLabel(teachingMode)} capture for ${tool.displayName}`,
      objective: requestedObjective || `Capture ${formatLabel(teachingMode)} evidence for ${tool.displayName}.`,
      teachingMode,
    });

    return session || activeSession;
  };

  const appendEvidence = async ({
    sessionId,
    kind,
    label,
    referenceUri,
  }: {
    sessionId: string;
    kind: AdminToolLearningSession["evidence"][number]["kind"];
    label: string;
    referenceUri: string;
  }) => {
    await onAppendLearningEvidence({
      sessionId,
      status: "ready_for_review",
      evidence: [
        {
          id: `evidence-${Date.now()}`,
          kind,
          label,
          referenceUri,
          sensitivity: "sensitive_sanitized",
        },
      ],
    });
  };

  const startCapture = async () => {
    if (!moduleId) return;
    setCaptureStatus("Preparing governed capture through Core.");
    const session = await ensureSession();
    if (!session) {
      setCaptureStatus("Create a learning session before capture can start.");
      return;
    }

    if (teachingMode === "screen_recording") {
      await startScreenRecording(session.id);
      return;
    }

    if (teachingMode === "document_upload") {
      documentInputRef.current?.click();
      setCaptureStatus("Choose a document to upload as sanitized learning evidence.");
      return;
    }

    if (teachingMode === "api_trace") {
      const trace = apiTrace.trim();
      if (!trace) {
        setCaptureStatus("Paste or import an API trace before starting API trace capture.");
        return;
      }
      await uploadTextArtifact({
        sessionId: session.id,
        kind: "api_trace",
        label: "Imported API trace",
        fileName: "api-trace.json",
        mimeType: "application/json",
        text: trace,
      });
      setCaptureStatus("API trace persisted and attached for review.");
      return;
    }

    if (teachingMode === "human_demonstration") {
      if (humanCaptureMode === "external_screen") {
        await startExternalHumanDemonstrationRecording(session.id);
        return;
      }

      if (humanCaptureMode === "controlled_browser") {
        const label = await onQueueOperatorCommand("browser_dry_run", {
          learningSessionId: session.id,
          requestedScopes: ["learning:human_demonstration", "learning:action_trace", "learning:controlled_browser"],
        });
        await appendEvidence({
          sessionId: session.id,
          kind: "human_feedback",
          label: "Controlled browser demonstration queued",
          referenceUri: `operator-runtime://browser-dry-run/${encodeURIComponent(label)}`,
        });
        setCaptureStatus("Controlled browser capture was queued. Dispatch Operator Runtime to attach browser action trace evidence.");
        return;
      }

      startWebActionTrace(session.id);
      setCaptureStatus("Portal trace is recording. This only captures actions inside Admin Console, not SAP/BAS in another tab.");
      return;
    }

    if (teachingMode === "routine_replay") {
      const label = await onQueueOperatorCommand("sap_browser_readiness_dry_run");
      await appendEvidence({
        sessionId: session.id,
        kind: "routine_replay",
        label: "Routine replay operator command started",
        referenceUri: `operator-runtime://routine-replay/${encodeURIComponent(label)}`,
      });
      setCaptureStatus("Routine replay routed through Operator Runtime and attached as evidence.");
      return;
    }

    await appendEvidence({
      sessionId: session.id,
      kind: "conversation",
      label: "Conversation teaching capture",
      referenceUri: `command-aura://learning/${session.id}`,
    });
    setCaptureStatus("Conversation teaching evidence is ready for review.");
  };

  const startScreenRecording = async (sessionId: string) => {
    if (!navigator.mediaDevices?.getDisplayMedia || typeof MediaRecorder === "undefined") {
      setCaptureStatus("Screen recording is unavailable in this browser.");
      return;
    }

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: false,
    });
    const chunks: BlobPart[] = [];
    const recorder = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : undefined,
      videoBitsPerSecond: 1_200_000,
    });

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      setRecording(null);
      void (async () => {
        try {
          const blob = new Blob(chunks, { type: "video/webm" });
          setCaptureStatus(`Saving screen recording through Core (${formatBytes(blob.size)}).`);
          await uploadBlobArtifact({
            sessionId,
            kind: "screen_recording",
            label: "Screen recording capture",
            fileName: "screen-recording.webm",
            blob,
          });
          setCaptureStatus("Screen recording stored through Core and attached for review.");
        } catch (error) {
          setCaptureStatus(`Screen recording was not stored: ${formatErrorMessage(error)}.`);
        }
      })();
    };

    recorder.start();
    setRecording(recorder);
    setCaptureStatus("Screen recording is active. Stop capture to store the artifact.");
  };

  const startExternalHumanDemonstrationRecording = async (sessionId: string) => {
    if (!navigator.mediaDevices?.getDisplayMedia || typeof MediaRecorder === "undefined") {
      setCaptureStatus("External screen capture is unavailable in this browser.");
      return;
    }

    const startedAt = new Date().toISOString();
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: false,
    });
    const chunks: BlobPart[] = [];
    const recorder = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : undefined,
      videoBitsPerSecond: 1_200_000,
    });

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      setRecording(null);
      void (async () => {
        try {
          const stoppedAt = new Date().toISOString();
          const blob = new Blob(chunks, { type: "video/webm" });
          setCaptureStatus(`Saving external demonstration through Core (${formatBytes(blob.size)}).`);
          await uploadBlobArtifact({
            sessionId,
            kind: "screen_recording",
            label: "External human demonstration recording",
            fileName: "human-demonstration.webm",
            blob,
          });
          await uploadTextArtifact({
            sessionId,
            kind: "action_trace",
            label: "External demonstration capture manifest",
            fileName: "external-demonstration-manifest.json",
            mimeType: "application/json",
            text: JSON.stringify({
              schemaVersion: 1,
              kind: "climb-one.external-human-demonstration",
              sessionId,
              toolId: tool.id,
              moduleId,
              teachingMode,
              startedAt,
              stoppedAt,
              source: "browser_display_media",
              captureBoundary: "visual_screen_recording",
              secretPolicy: {
                rawInputValuesStored: false,
                credentialsAllowed: false,
                providerNativeMemoryAllowed: false,
              },
              note: "This captures the selected screen/window/tab visually. DOM selectors and click replay require controlled browser capture.",
            }, null, 2),
          });
          setCaptureStatus("External human demonstration stored. Review the video evidence; use controlled browser capture when DOM replay is required.");
        } catch (error) {
          setCaptureStatus(`External demonstration was not stored: ${formatErrorMessage(error)}.`);
        }
      })();
    };

    recorder.start();
    setRecording(recorder);
    setCaptureStatus("External capture is active. Switch to SAP/BAS/Integration Suite and perform the routine, then return here and stop capture.");
  };

  const startWebActionTrace = (sessionId: string) => {
    const startedAt = new Date().toISOString();
    actionTraceEventsRef.current = [
      {
        id: `trace-event-${Date.now()}-start`,
        kind: "navigate",
        capturedAt: startedAt,
        url: window.location.href,
        title: document.title,
      },
    ];

    const capture = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      const pointer = "clientX" in event && "clientY" in event
        ? {
            x: Math.round(Number((event as MouseEvent).clientX)),
            y: Math.round(Number((event as MouseEvent).clientY)),
          }
        : undefined;
      const valueLength = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
        ? target.value.length
        : undefined;

      actionTraceEventsRef.current.push({
        id: `trace-event-${Date.now()}-${actionTraceEventsRef.current.length}`,
        kind: event.type === "click"
          ? "click"
          : event.type === "submit"
            ? "submit"
            : "input",
        capturedAt: new Date().toISOString(),
        url: window.location.href,
        title: document.title,
        selector: target ? describeElementSelector(target) : undefined,
        text: target ? safeVisibleText(target) : undefined,
        role: target?.getAttribute("role") ?? undefined,
        tagName: target?.tagName.toLowerCase(),
        href: target instanceof HTMLAnchorElement ? target.href : undefined,
        pointer,
        valueLength,
        valueRedacted: valueLength !== undefined,
      });
    };

    document.addEventListener("click", capture, true);
    document.addEventListener("input", capture, true);
    document.addEventListener("change", capture, true);
    document.addEventListener("submit", capture, true);

    stopActionTraceRef.current = () => {
      document.removeEventListener("click", capture, true);
      document.removeEventListener("input", capture, true);
      document.removeEventListener("change", capture, true);
      document.removeEventListener("submit", capture, true);
    };
    setActionTraceRecording({ sessionId, startedAt });
  };

  const stopWebActionTrace = async () => {
    if (!actionTraceRecording) return;
    stopActionTraceRef.current?.();
    stopActionTraceRef.current = null;
    const stoppedAt = new Date().toISOString();
    const trace: WebActionTraceArtifact = {
      schemaVersion: 1,
      kind: "climb-one.web-action-trace",
      sessionId: actionTraceRecording.sessionId,
      toolId: tool.id,
      moduleId,
      teachingMode,
      startedAt: actionTraceRecording.startedAt,
      stoppedAt,
      source: "admin_console_browser_surface",
      secretPolicy: {
        rawInputValuesStored: false,
        credentialsAllowed: false,
        providerNativeMemoryAllowed: false,
      },
      events: actionTraceEventsRef.current,
    };
    await uploadTextArtifact({
      sessionId: actionTraceRecording.sessionId,
      kind: "action_trace",
      label: "Web action trace",
      fileName: "action-trace.json",
      mimeType: "application/json",
      text: JSON.stringify(trace, null, 2),
    });
    setActionTraceRecording(null);
    actionTraceEventsRef.current = [];
    setCaptureStatus("Action trace stored through Core. You can replay or review it from the evidence viewer.");
  };

  const uploadDocument = async (file: File | undefined) => {
    const session = await ensureSession();
    if (!file || !session) return;
    await uploadBlobArtifact({
      sessionId: session.id,
      kind: file.type.startsWith("text/") ? "document" : "document",
      label: file.name,
      fileName: file.name,
      blob: file,
    });
    setCaptureStatus("Document uploaded and attached for review.");
  };

  const uploadTextArtifact = async ({
    sessionId,
    kind,
    label,
    fileName,
    mimeType,
    text,
  }: {
    sessionId: string;
    kind: AdminToolLearningSession["evidence"][number]["kind"];
    label: string;
    fileName: string;
    mimeType: string;
    text: string;
  }) => {
    await onUploadLearningArtifact({
      sessionId,
      kind,
      label,
      fileName,
      mimeType,
      contentBase64: btoa(unescape(encodeURIComponent(text))),
      status: "ready_for_review",
    });
  };

  const uploadBlobArtifact = async ({
    sessionId,
    kind,
    label,
    fileName,
    blob,
  }: {
    sessionId: string;
    kind: AdminToolLearningSession["evidence"][number]["kind"];
    label: string;
    fileName: string;
    blob: Blob;
  }) => {
    await onUploadLearningArtifact({
      sessionId,
      kind,
      label,
      fileName,
      mimeType: blob.type || "application/octet-stream",
      contentBase64: await blobToBase64(blob),
      status: "ready_for_review",
    });
  };

  const replaySession = async (session: AdminToolLearningSession) => {
    const replayableEvidence = session.evidence.filter(isReplayableLearningEvidence);
    const hasScreenRecording = session.evidence.some((evidence) => evidence.kind === "screen_recording");

    if (replayableEvidence.length === 0) {
      setCaptureStatus(
        hasScreenRecording
          ? `${session.title} has video evidence for review, but no action trace for automated replay. Use Controlled browser capture when you need click replay.`
          : `${session.title} has no replayable evidence yet. Start capture first, then stop capture so Core can store a video or action trace.`
      );
      return;
    }

    setCaptureStatus(`Queued governed replay for ${session.title}.`);
    const label = await onQueueOperatorCommand(
      session.toolId === "tool-sap" ? "sap_browser_readiness_dry_run" : "browser_dry_run",
      {
        learningSessionId: session.id,
        requestedScopes: ["learning:routine_replay", "learning:evidence_replay"],
      }
    );
    await onAppendLearningEvidence({
      sessionId: session.id,
      status: "ready_for_review",
      evidence: [
        {
          id: `evidence-replay-${Date.now()}`,
          kind: "routine_replay",
          label: "Governed replay evidence",
          referenceUri: `operator-runtime://routine-replay/${encodeURIComponent(label)}`,
          sensitivity: "sensitive_sanitized",
        },
      ],
    });
    setCaptureStatus("Replay evidence was attached. Review the result before promoting memory.");
  };

  const requestReview = async (session: AdminToolLearningSession) => {
    await onPromoteLearningMemory({
      sessionId: session.id,
      promotionStatus: "review_required",
    });
    setCaptureStatus(`${session.title} was sent to memory review. Open its evidence before approving memory.`);
  };

  const approveMemory = async (session: AdminToolLearningSession) => {
    await onPromoteLearningMemory({
      sessionId: session.id,
      promotionStatus: "approved",
    });
    setCaptureStatus(`${session.title} is approved for memory promotion.`);
  };

  return (
    <section className="rounded-2xl border border-border/35 bg-card/35 p-4">
      <PanelTitle icon={GraduationCap} title="Teaching / Learning Studio" detail="Capture evidence for tenant-scoped expert learning." />
      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {teachingModes.map((mode) => (
            <button
              key={mode}
              onClick={() => setTeachingMode(mode)}
              className={cn(
                "rounded-xl border p-3 text-left text-sm transition",
                teachingMode === mode
                  ? "border-cyan-300/40 bg-cyan-300/10 text-foreground"
                  : "border-border/25 bg-background/25 text-muted-foreground hover:text-foreground"
              )}
            >
              {formatLabel(mode)}
            </button>
          ))}
        </div>
        <div className="space-y-3 rounded-xl border border-border/25 bg-background/25 p-3">
          <SelectField label="Module" value={moduleId} onChange={setModuleId}>
            {tool.modules.map((module) => (
              <option key={module.id} value={module.id}>
                {module.displayName}
              </option>
            ))}
          </SelectField>
          <TextField label="Title" value={title} onChange={setTitle} placeholder="Routine or teaching session title" />
          <TextField label="Objective" value={objective} onChange={setObjective} placeholder="What the agent should learn" />
          {teachingMode === "api_trace" && (
            <textarea
              value={apiTrace}
              onChange={(event) => setApiTrace(event.target.value)}
              placeholder="Paste sanitized API trace or HAR JSON. Secrets are rejected by Core."
              className="min-h-24 w-full rounded-md border border-border/50 bg-background/60 px-3 py-2 text-sm text-foreground outline-none focus:border-cyan-300/50"
            />
          )}
          {teachingMode === "human_demonstration" && (
            <SelectField
              label="Human demonstration capture"
              value={humanCaptureMode}
              onChange={(value) => setHumanCaptureMode(value as "external_screen" | "controlled_browser" | "portal_trace")}
            >
              <option value="external_screen">External screen / SAP GUI / BAS window</option>
              <option value="controlled_browser">Controlled browser with action trace</option>
              <option value="portal_trace">Admin Console portal trace only</option>
            </SelectField>
          )}
          <input
            ref={documentInputRef}
            type="file"
            className="hidden"
            onChange={(event) => {
              void uploadDocument(event.target.files?.[0]);
              event.currentTarget.value = "";
            }}
          />
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              disabled={!moduleId}
              onClick={() => void ensureSession()}
            >
              Create session
            </Button>
            {recording ? (
              <Button onClick={() => recording.stop()}>
                Stop Capture
              </Button>
            ) : actionTraceRecording ? (
              <Button onClick={() => void stopWebActionTrace()}>
                Stop Capture
              </Button>
            ) : (
              <Button disabled={!moduleId} onClick={() => void startCapture()}>
                Start Capture
              </Button>
            )}
          </div>
          {activeSession && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() =>
                onPromoteLearningMemory({
                  sessionId: activeSession.id,
                  promotionStatus: "review_required",
                })
              }
            >
              Send to memory review
            </Button>
          )}
          {captureStatus && (
            <p className="rounded-lg border border-border/25 bg-background/25 p-2 text-xs text-muted-foreground">
              {captureStatus}
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 grid gap-3">
        {sessions.slice(0, 10).map((session) => (
          <LearningSessionEvidenceViewer
            key={session.id}
            session={session}
            onReplay={() => void replaySession(session)}
            onRequestReview={() => void requestReview(session)}
            onApproveMemory={() => void approveMemory(session)}
          />
        ))}
      </div>
    </section>
  );
}

function LearningSessionEvidenceViewer({
  session,
  onReplay,
  onRequestReview,
  onApproveMemory,
}: {
  session: AdminToolLearningSession;
  onReplay: () => void;
  onRequestReview: () => void;
  onApproveMemory: () => void;
}) {
  const playableEvidence = session.evidence.filter((evidence) => Boolean(adminToolLearningArtifactUrl(evidence.referenceUri)));
  const replayableEvidence = session.evidence.filter(isReplayableLearningEvidence);
  const actionTraceCount = session.evidence.filter((evidence) => evidence.kind === "action_trace").length;

  return (
    <article className="rounded-xl border border-border/25 bg-background/25 p-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{session.title}</span>
            <StatusPill value={session.status} tone="amber" />
            <StatusPill value={session.memoryWriteback.promotionStatus} tone="cyan" />
          </div>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{session.objective}</p>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {formatLabel(session.teachingMode)} · {session.evidence.length} evidence refs · {actionTraceCount} action traces
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={replayableEvidence.length === 0} onClick={onReplay}>
            <PlayCircle className="mr-2 h-3.5 w-3.5" />
            Replay
          </Button>
          <Button size="sm" variant="outline" onClick={onRequestReview}>
            <Eye className="mr-2 h-3.5 w-3.5" />
            Review
          </Button>
          <Button size="sm" onClick={onApproveMemory}>
            <CheckCircle2 className="mr-2 h-3.5 w-3.5" />
            Approve memory
          </Button>
        </div>
      </div>

      {playableEvidence.length > 0 && (
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {playableEvidence.map((evidence) => (
            <EvidenceArtifactPreview key={evidence.id} evidence={evidence} />
          ))}
        </div>
      )}
      {playableEvidence.length === 0 && (
        <p className="mt-3 rounded-lg border border-amber-300/20 bg-amber-300/10 p-3 text-xs leading-5 text-amber-100">
          No playable artifact is attached yet. Start capture and stop capture to store video or use controlled browser capture to store an action trace.
        </p>
      )}

      <div className="mt-3 grid gap-2">
        {session.evidence.map((evidence) => (
          <div key={evidence.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/20 bg-card/20 px-3 py-2 text-xs">
            <div className="flex min-w-0 items-center gap-2">
              <EvidenceKindIcon kind={evidence.kind} />
              <div className="min-w-0">
                <p className="truncate text-foreground">{evidence.label}</p>
                <p className="truncate text-muted-foreground">{evidence.referenceUri}</p>
              </div>
            </div>
            {adminToolLearningArtifactUrl(evidence.referenceUri) && (
              <a
                href={adminToolLearningArtifactUrl(evidence.referenceUri) ?? undefined}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-cyan-200 hover:text-cyan-100"
              >
                Open
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        ))}
      </div>
    </article>
  );
}

function isReplayableLearningEvidence(evidence: AdminToolLearningSession["evidence"][number]): boolean {
  const artifactUrl = adminToolLearningArtifactUrl(evidence.referenceUri);
  return evidence.kind === "action_trace" && Boolean(artifactUrl) && /\.json($|\?)/i.test(artifactUrl);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const megabytes = bytes / 1024 / 1024;
  if (megabytes >= 1) return `${megabytes.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function formatErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Core rejected the artifact upload";
}

function EvidenceArtifactPreview({
  evidence,
}: {
  evidence: AdminToolLearningSession["evidence"][number];
}) {
  const url = adminToolLearningArtifactUrl(evidence.referenceUri);
  if (!url) return null;

  if (evidence.kind === "action_trace" && /\.json($|\?)/i.test(url)) {
    return <ActionTracePreview evidence={evidence} url={url} />;
  }

  if (evidence.kind === "screen_recording" || /\.webm($|\?)/i.test(url)) {
    return (
      <div className="rounded-lg border border-border/20 bg-black/30 p-2">
        <video src={url} controls className="aspect-video w-full rounded-md bg-black" />
        <p className="mt-2 text-xs text-muted-foreground">{evidence.label}</p>
      </div>
    );
  }

  if (evidence.kind === "screenshot" || /\.(png|jpe?g)($|\?)/i.test(url)) {
    return (
      <div className="rounded-lg border border-border/20 bg-black/30 p-2">
        <img src={url} alt={evidence.label} className="aspect-video w-full rounded-md object-contain" />
        <p className="mt-2 text-xs text-muted-foreground">{evidence.label}</p>
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center justify-between gap-3 rounded-lg border border-border/20 bg-card/20 p-3 text-xs text-muted-foreground hover:text-foreground"
    >
      <span className="flex items-center gap-2">
        <FileText className="h-4 w-4" />
        {evidence.label}
      </span>
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

function ActionTracePreview({
  evidence,
  url,
}: {
  evidence: AdminToolLearningSession["evidence"][number];
  url: string;
}) {
  const [trace, setTrace] = useState<WebActionTraceArtifact | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    fetch(url, { cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error("action_trace_fetch_failed");
        return response.json();
      })
      .then((payload) => {
        if (!cancelled) setTrace(normalizeActionTracePayload(payload));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  const events = trace?.events ?? [];

  return (
    <div className="rounded-lg border border-cyan-300/20 bg-cyan-300/10 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-medium text-cyan-100">
            <MousePointerClick className="h-4 w-4" />
            Action trace
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {failed
              ? "Trace could not be loaded."
              : trace
                ? `${events.length} captured events · values redacted`
                : "Loading trace events..."}
          </p>
        </div>
        <a href={url} target="_blank" rel="noreferrer" className="text-xs text-cyan-100 hover:text-white">
          Open JSON
        </a>
      </div>
      <div className="mt-3 space-y-2">
        {events.slice(0, 6).map((event) => (
          <div key={event.id} className="rounded-md border border-border/20 bg-background/30 px-3 py-2 text-xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium text-foreground">{formatLabel(event.kind)}</span>
              <span className="text-[11px] text-muted-foreground">{formatShortTime(event.capturedAt)}</span>
            </div>
            <p className="mt-1 truncate text-muted-foreground">{event.selector ?? event.url}</p>
            {event.text && <p className="mt-1 truncate text-cyan-100">{event.text}</p>}
          </div>
        ))}
        {trace && events.length === 0 && (
          <p className="rounded-md border border-amber-300/20 bg-amber-300/10 px-3 py-2 text-xs text-amber-100">
            No click/input events were captured. This usually means the activity happened outside this web surface.
          </p>
        )}
      </div>
      <p className="mt-3 text-[11px] leading-5 text-muted-foreground">
        {evidence.label}
      </p>
    </div>
  );
}

function EvidenceKindIcon({ kind }: { kind: AdminToolLearningSession["evidence"][number]["kind"] }) {
  if (kind === "screen_recording") return <Video className="h-4 w-4 text-cyan-200" />;
  if (kind === "action_trace") return <MousePointerClick className="h-4 w-4 text-cyan-200" />;
  if (kind === "document" || kind === "api_trace") return <FileText className="h-4 w-4 text-amber-200" />;
  if (kind === "routine_replay") return <PlayCircle className="h-4 w-4 text-cyan-200" />;
  return <Waypoints className="h-4 w-4 text-muted-foreground" />;
}

function normalizeActionTracePayload(payload: unknown): WebActionTraceArtifact {
  const trace = payload as Partial<WebActionTraceArtifact> & {
    events?: unknown;
  };
  return {
    schemaVersion: 1,
    kind: "climb-one.web-action-trace",
    sessionId: typeof trace.sessionId === "string" ? trace.sessionId : "unknown",
    toolId: typeof trace.toolId === "string" ? trace.toolId : "unknown",
    moduleId: typeof trace.moduleId === "string" ? trace.moduleId : "unknown",
    teachingMode: typeof trace.teachingMode === "string" ? trace.teachingMode as AdminToolTeachingMode : "human_demonstration",
    startedAt: typeof trace.startedAt === "string" ? trace.startedAt : "",
    stoppedAt: typeof trace.stoppedAt === "string" ? trace.stoppedAt : "",
    source: "admin_console_browser_surface",
    secretPolicy: {
      rawInputValuesStored: false,
      credentialsAllowed: false,
      providerNativeMemoryAllowed: false,
    },
    events: Array.isArray(trace.events)
      ? trace.events.map(normalizeActionTraceEvent).filter((event): event is WebActionTraceEvent => Boolean(event))
      : [],
  };
}

function normalizeActionTraceEvent(value: unknown): WebActionTraceEvent | null {
  if (!value || typeof value !== "object") return null;
  const event = value as Partial<WebActionTraceEvent>;
  if (typeof event.kind !== "string" || typeof event.capturedAt !== "string") return null;
  return {
    id: typeof event.id === "string" ? event.id : `event-${event.capturedAt}`,
    kind: event.kind === "click" || event.kind === "input" || event.kind === "submit" ? event.kind : "navigate",
    capturedAt: event.capturedAt,
    url: typeof event.url === "string" ? event.url : "",
    title: typeof event.title === "string" ? event.title : "",
    selector: typeof event.selector === "string" ? event.selector : undefined,
    text: typeof event.text === "string" ? event.text : undefined,
    role: typeof event.role === "string" ? event.role : undefined,
    tagName: typeof event.tagName === "string" ? event.tagName : undefined,
    href: typeof event.href === "string" ? event.href : undefined,
    pointer: event.pointer,
    valueLength: typeof event.valueLength === "number" ? event.valueLength : undefined,
    valueRedacted: event.valueRedacted === true,
  };
}

function formatShortTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function describeElementSelector(element: Element): string {
  const testId = element.getAttribute("data-testid") ?? element.getAttribute("data-uat");
  if (testId) return `[data-testid="${cssEscapeValue(testId)}"], [data-uat="${cssEscapeValue(testId)}"]`;
  if (element.id) return `#${cssEscapeValue(element.id)}`;
  const name = element.getAttribute("name");
  if (name) return `${element.tagName.toLowerCase()}[name="${cssEscapeValue(name)}"]`;
  const aria = element.getAttribute("aria-label");
  if (aria) return `${element.tagName.toLowerCase()}[aria-label="${cssEscapeValue(aria)}"]`;
  const classes = Array.from(element.classList).slice(0, 3).map((item) => `.${cssEscapeValue(item)}`).join("");
  return `${element.tagName.toLowerCase()}${classes}`;
}

function cssEscapeValue(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/["\\]/g, "\\$&").replace(/[^a-zA-Z0-9_-]/g, "-");
}

function safeVisibleText(element: Element): string | undefined {
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    return undefined;
  }
  const text = (element.textContent ?? "").replace(/\s+/g, " ").trim();
  if (!text) return undefined;
  return text.slice(0, 160);
}

function MemoryConsolidationPanel({
  assignments,
  consolidationRuns,
}: {
  assignments: AdminToolAgentAssignment[];
  consolidationRuns: AdminToolConsolidationRun[];
}) {
  const daemon = assignments[0]?.daemon;
  const phases = daemon?.cognitiveConsolidation.phases ?? [];

  return (
    <section className="rounded-2xl border border-border/35 bg-card/35 p-4">
      <PanelTitle icon={Moon} title="Memory consolidation" detail="Sleep-like cognitive consolidation for tool agents." />
      {daemon ? (
        <div className="mt-4 space-y-3">
          <div className="rounded-xl border border-border/25 bg-background/25 p-3 text-xs text-muted-foreground">
            <p>
              Schedule: <span className="text-foreground">{formatLabel(daemon.cognitiveConsolidation.schedule.cadence)}</span>
            </p>
            <p>
              Window: <span className="text-foreground">{daemon.cognitiveConsolidation.schedule.preferredWindow}</span>
            </p>
            <p className="mt-2 text-amber-200">
              Raw secrets and provider-native memory are not allowed.
            </p>
          </div>
          {phases.map((phase) => (
            <div key={phase.kind} className="rounded-lg border border-border/25 bg-background/25 p-3">
              <p className="text-sm font-medium text-foreground">{formatLabel(phase.kind)}</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{phase.purpose}</p>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Writes: {phase.outputArtifacts.join(", ")}
              </p>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Consolidation runs: <span className="text-foreground">{consolidationRuns.length}</span>
          </p>
        </div>
      ) : (
        <p className="mt-4 rounded-lg border border-border/25 bg-background/25 p-3 text-sm text-muted-foreground">
          Assign an agent to create a daemon and memory consolidation contract.
        </p>
      )}
    </section>
  );
}

function RoleVisibilityControls({ tool }: { tool: AdminToolCatalogRecord }) {
  return (
    <section className="rounded-2xl border border-border/35 bg-card/35 p-4">
      <PanelTitle icon={Lock} title="Role / visibility controls" detail="Prepared for tenant role administration." />
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <MetricCard icon={Eye} label="Visibility" value={formatLabel(tool.visibility)} />
        <MetricCard icon={ShieldCheck} label="Allowed roles" value={tool.allowedRoleIds.length} />
      </div>
      <ChipRow values={tool.allowedRoleIds} />
    </section>
  );
}

function EmptyTools({ loading }: { loading?: boolean }) {
  return (
    <section className="rounded-2xl border border-border/35 bg-card/35 p-8 text-center">
      <Database className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-4 text-lg font-semibold text-foreground">
        {loading ? "Loading tools from Core" : "No tools returned by Core"}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        This surface does not invent tool records. It waits for `/admin-console/tools`.
      </p>
    </section>
  );
}

function PanelTitle({ icon: Icon, title, detail }: { icon: typeof Brain; title: string; detail: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="rounded-lg border border-cyan-300/20 bg-cyan-300/10 p-2 text-cyan-200">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{detail}</p>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value }: { icon: typeof Brain; label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-border/25 bg-background/25 p-3">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <p className="mt-3 text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-background/25 px-2 py-1">
      <span className="text-muted-foreground">{label}: </span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function StatusPill({ value, tone = "default" }: { value: string; tone?: "default" | "muted" | "cyan" | "amber" }) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em]",
        tone === "cyan"
          ? "border-cyan-300/30 bg-cyan-300/10 text-cyan-100"
          : tone === "amber"
          ? "border-amber-300/30 bg-amber-300/10 text-amber-100"
          : tone === "muted"
          ? "border-border/40 bg-muted/20 text-muted-foreground"
          : "border-emerald-300/25 bg-emerald-300/10 text-emerald-100"
      )}
    >
      {formatLabel(value)}
    </span>
  );
}

function ChipRow({ values }: { values: string[] }) {
  if (values.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {values.slice(0, 8).map((value) => (
        <span key={value} className="rounded-full border border-border/30 bg-background/30 px-2 py-0.5 text-[11px] text-muted-foreground">
          {formatLabel(value)}
        </span>
      ))}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 w-full rounded-md border border-border/50 bg-background/60 px-3 text-sm text-foreground outline-none focus:border-cyan-300/50"
      >
        {children}
      </select>
    </label>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded-md border border-border/50 bg-background/60 px-3 text-sm text-foreground outline-none focus:border-cyan-300/50"
      />
    </label>
  );
}

function groupModules(modules: AdminToolModuleContract[]) {
  const categories: AdminToolModuleContract["category"][] = ["technical", "functional", "hybrid"];
  return categories.map((category) => ({
    category,
    modules: modules.filter((module) => module.category === category),
  })).filter((group) => group.modules.length > 0);
}

function unique<T>(values: T[]): T[] {
  return [...new Set(values)];
}

function splitCsv(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function slugInput(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.includes(",") ? result.split(",").pop() ?? "" : result);
    };
    reader.readAsDataURL(blob);
  });
}

function formatLabel(value: string | undefined): string {
  if (!value) return "Not set";
  return value
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(value: string | undefined): string {
  if (!value) return "not scheduled";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
